import React from 'react'
import {
  assets,
  AuthService,
  DatePicker,
  Input,
  PolicyDispatchingPermission,
  RadioButton,
  Select,
  SelectSearch
} from '@pulseops/common'
import { Controller, useForm } from 'react-hook-form'
import { ActivityIndicator, Text, View } from 'react-native'
import { ZIO } from '@mxt/zio'
import { pipe } from 'fp-ts/function'
import {
  borderedDatePickerContainerStyle,
  borderedInputStyle,
  borderedSelectStyle,
  borderedSelectSearchStyle
} from '../styles/policyDispatchingStyles'
import PolicyDispatchingDialog from './PolicyDispatchingDialog'
import { PolicyDispatchingService } from '../services/PolicyDispatchingService'
import { usePolicyDispatchingOffice } from './PolicyDispatchingOfficeContext'
import { formatDate, getPermissions } from '../utils/policyDispatchingUtils'

type SelectOption = { value: string; label: string }

export default function PDCreateAuthorizationLetter({ styles }: { styles: { [key: string]: any } }) {
  const [agentNumber, setAgentNumber] = React.useState('')
  const [agentGroups, setAgentGroups] = React.useState<SelectOption[]>([])
  const [approvers, setApprovers] = React.useState<SelectOption[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [modalInfo, setModalInfo] = React.useState<{ title: string; message: React.ReactNode }>({
    title: '',
    message: ''
  })
  const [openModal, setOpenModal] = React.useState(false)
  const [isAgent, setIsAgent] = React.useState(true)
  const { officeCode } = usePolicyDispatchingOffice()
  const { control, setValue, watch, getValues, reset } = useForm<any>({
    defaultValues: {
      agentNumber: '',
      agentName: '',
      type: '',
      dateAppointed: '',
      dateTerminate: '',
      status: '',
      agentSalesUnitCode: '',
      area: '',
      gaBp: '',
      agentSalesBranch: '',
      saleZone: '',
      branch: '',
      authorizedPerson: '',
      authorizedAgent: '',
      approver: null,
      effectiveFromDate: null,
      effectiveToDate: null,
      agentGroup: []
    }
  })
  const currentAgentNumber = watch('agentNumber') || agentNumber
  const permissions = getPermissions()
  const allowCreate = permissions.includes(PolicyDispatchingPermission.AuthManagementCreate)

  const handleSearch = () => {
    if (!agentNumber) {
      setModalInfo({ title: 'Tìm kiếm', message: 'Vui lòng nhập mã số đại lý trước khi tìm kiếm.' })
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.searchAuthorizationLetterAgent(agentNumber),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          if (!('body' in response)) {
            setIsLoading(false)
            setValue('agentName', '')
            setValue('agentSalesBranch', '')
            setValue('agentSalesUnitCode', '')
            setValue('area', '')
            setValue('branch', '')
            setValue('dateAppointed', '')
            setValue('dateTerminate', '')
            setValue('gaBp', '')
            setValue('saleZone', '')
            setValue('status', '')
            setValue('type', '')
            setValue('authorizedAgent', '')
            setValue('agentGroup', [])
            setValue('approver', null)
            setAgentGroups([])
            setApprovers([])
            setModalInfo({
              title: 'Tìm kiếm',
              message: 'Không tìm thấy thông tin được yêu cầu!'
            })
            setOpenModal(true)
            return
          }

          const information = response.body.agentInformation
          setAgentNumber(information.agentNumber)
          setValue('agentNumber', information.agentNumber)
          setValue('authorizedAgent', information.agentNumber)
          setValue('agentName', information.agentName)
          setValue('agentSalesBranch', information.agentSalesBranch)
          setValue('agentSalesUnitCode', information.agentSalesUnitCode)
          setValue('area', information.area)
          setValue('branch', information.branch)
          setValue('dateAppointed', information.dateAppointed)
          setValue('dateTerminate', information.dateTerminate || '')
          setValue('gaBp', information.gaBp)
          setValue('saleZone', information.saleZone)
          setValue('status', information.status)
          setValue('type', information.type)
          setAgentGroups(response.body.agentGroups.map((value) => ({ value, label: value })))
          setApprovers(response.body.approvers.map((value) => ({ value, label: value })))
          setValue('agentGroup', [])
          setValue('approver', null)
          setIsLoading(false)
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          setAgentGroups([])
          setApprovers([])
          setModalInfo({ title: 'Tìm kiếm thất bại', message: 'Không thể tải thông tin đại lý. Vui lòng thử lại sau.' })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleCreate = () => {
    if (!officeCode) {
      setModalInfo({ title: 'Văn phòng', message: 'Vui lòng chọn văn phòng trước khi tạo.' })
      setOpenModal(true)
      return
    }

    const values = getValues()
    const agentGroupValues: string[] = (values.agentGroup || []).map((item: SelectOption) => item.value)
    const hasAgentOrGroup = isAgent ? Boolean(values.authorizedAgent) : agentGroupValues.length > 0

    if (
      !hasAgentOrGroup ||
      !values.authorizedPerson ||
      !values.approver?.value ||
      !values.effectiveFromDate ||
      !values.effectiveToDate
    ) {
      setModalInfo({ title: 'Tạo giấy ủy quyền', message: 'Vui lòng nhập đầy đủ thông tin bắt buộc.' })
      setOpenModal(true)
      return
    }

    const effectiveFromDay = Date.UTC(
      values.effectiveFromDate.getFullYear(),
      values.effectiveFromDate.getMonth(),
      values.effectiveFromDate.getDate()
    )
    const effectiveToDay = Date.UTC(
      values.effectiveToDate.getFullYear(),
      values.effectiveToDate.getMonth(),
      values.effectiveToDate.getDate()
    )
    const effectiveDurationDays = (effectiveToDay - effectiveFromDay) / (24 * 60 * 60 * 1000)

    if (effectiveDurationDays > 60) {
      setModalInfo({
        title: 'Tạo giấy ủy quyền',
        message: 'Thời gian giới hạn của giấy uỷ quyền phải trong khoảng 60 ngày'
      })
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    pipe(
      AuthService.userInfo,
      ZIO.flatMap((userInfo) =>
        PolicyDispatchingService.createAuthorizationLetter(officeCode, {
          authorizedPerson: values.authorizedPerson,
          approver: values.approver.value,
          effectiveFromDate: formatDate(values.effectiveFromDate),
          effectiveToDate: formatDate(values.effectiveToDate),
          agentNumber: values.agentNumber || '',
          agentGroup: agentGroupValues,
          owner: userInfo.name || userInfo.email || '',
          authorizer: isAgent ? values.agentName : ''
        })
      ),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          const authorizationLetterId = (response as { body?: { authorizationLetterId?: string } }).body
            ?.authorizationLetterId
          setIsLoading(false)
          reset()
          setAgentNumber('')
          setModalInfo({
            title: 'Tạo giấy ủy quyền',
            message: authorizationLetterId ? (
              <>
                Tạo giấy ủy quyền thành công. Giấy ủy quyền mới có mã là{' '}
                <Text style={{ fontWeight: 'bold' }}>{authorizationLetterId}</Text>
              </>
            ) : (
              'Tạo giấy ủy quyền thành công.'
            )
          })
          setOpenModal(true)
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          setModalInfo({ title: 'Tạo giấy ủy quyền', message: 'Không thể tạo giấy ủy quyền. Vui lòng thử lại sau.' })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  return (
    <View style={{ display: 'flex', gap: 24, flexDirection: 'column' } as any}>
      <PolicyDispatchingDialog
        visible={openModal}
        title={modalInfo.title}
        message={modalInfo.message}
        onClose={() => setOpenModal(false)}
      />
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#ED1B2E" />
        </View>
      )}
      <View style={styles.row}>
        <View style={styles.column_1_4}>
          <Controller
            name="agentNumber"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Mã số đại lý'}
                onChange={(value) => {
                  setAgentNumber(value)
                  onChange(value)
                  if (isAgent) {
                    setValue('authorizedAgent', value)
                  }
                }}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                containerStyle={{ flex: 1 }}
                inputStyle={borderedInputStyle}
                filled
                required
                maxLength={8}
              />
            )}
          />
        </View>
        <View style={{ marginTop: 20 } as any}>
          <button style={styles.submitButton} type="button" onClick={handleSearch} disabled={isLoading}>
            <span style={styles.mr_5}>
              <assets.SearchWhite />
            </span>
            Tìm
          </button>
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_4}>
          <Controller
            name="agentName"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Tên đại lý'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="type"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Loại'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="dateAppointed"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Ngày gia nhập'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                containerStyle={{ flex: 1 }}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="dateTerminate"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Ngày kết thúc'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                containerStyle={{ flex: 1 }}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_4}>
          <Controller
            name="status"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Tình trạng'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="agentSalesUnitCode"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Nhóm'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="area"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Khu vực'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                containerStyle={{ flex: 1 }}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4} />
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_4}>
          <Controller
            name="gaBp"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'GA/BP'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="agentSalesBranch"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Ban'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="saleZone"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Vùng'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                containerStyle={{ flex: 1 }}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
        <View style={styles.column_1_4} />
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_4}>
          <Controller
            name="branch"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Văn phòng'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                disabled
                alwayShowUnderline
              />
            )}
          />
        </View>
      </View>

      <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 30 } as any}>
        <Text style={styles.subTitle}> - Thông tin ủy quyền</Text>
        <View
          style={
            {
              borderBottomWidth: 1,
              borderBottomStyle: 'solid',
              borderBottomColor: '#ccc',
              flex: 1,
              marginTop: 4
            } as any
          }
        />
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <Controller
            name="authorizedPerson"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Người được ủy quyền'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                required
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
              />
            )}
          />
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <Controller
            name="approver"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Select
                label={'Người phê duyệt'}
                options={approvers}
                placeholder={'Chọn người phê duyệt'}
                onChange={(e) => {
                  onChange(e)
                }}
                onBlur={onBlur}
                value={value}
                key={value?.value}
                errorMessage={error?.message}
                containerStyles={borderedSelectStyle}
                disabled={approvers.length === 0}
                filled
                required
              />
            )}
          />
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_4}>
          <Controller
            name="effectiveFromDate"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <DatePicker
                label={'Hiệu lực từ ngày'}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                maxDate={undefined}
                minDate={undefined}
                minDateMessage={''}
                errorMessage={error?.message}
                hideUnderline
                containerStyle={borderedDatePickerContainerStyle}
                inputStyle={{ paddingBottom: 3 }}
                required
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="effectiveToDate"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <DatePicker
                label={'Đến ngày'}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                maxDate={undefined}
                minDate={undefined}
                minDateMessage={''}
                errorMessage={error?.message}
                hideUnderline
                containerStyle={borderedDatePickerContainerStyle}
                inputStyle={{ paddingBottom: 3 }}
                required
              />
            )}
          />
        </View>
      </View>

      <View>
        <Text style={{ fontSize: '14px', marginBottom: '10px' } as any}> - Được phép nhận hợp đồng của</Text>
        <View style={{ ...styles.row, marginBottom: 10 }}>
          <RadioButton
            label={'Đai lý'}
            value="agent"
            selected={isAgent}
            style={{ marginRight: 28, marginBottom: 5 }}
            onChange={() => {
              setValue('agentGroup', [])
              setValue('authorizedAgent', currentAgentNumber)
              setIsAgent(true)
            }}
          />
          <RadioButton
            label={'Nhóm'}
            value="group"
            selected={!isAgent}
            onChange={() => {
              setValue('agentGroup', [])
              setValue('authorizedAgent', '')
              setIsAgent(false)
            }}
          />
        </View>
        <View style={styles.row}>
          {isAgent ? (
            <View style={styles.column_1_4}>
              <Input
                title={'Đại lý'}
                disabled
                value={currentAgentNumber || ''}
                inputStyle={borderedInputStyle}
                filled
                alwayShowUnderline
              />
            </View>
          ) : (
            <View style={styles.column_1_4}>
              <Controller
                name="agentGroup"
                control={control}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <SelectSearch
                    label={'Nhóm'}
                    options={agentGroups}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={Array.isArray(value) ? value : []}
                    key={JSON.stringify(Array.isArray(value) ? value : [])}
                    errorMessage={error?.message}
                    inputStyle={[
                      borderedSelectSearchStyle,
                      (!agentGroups || agentGroups.length === 0) && { backgroundColor: '#f3f4f6' }
                    ]}
                    disableUnderline
                    isShowFullText={true}
                    multiple
                    required
                    disabled={!agentGroups || agentGroups.length === 0}
                  />
                )}
              />
            </View>
          )}
        </View>
        {currentAgentNumber && !isAgent && (!agentGroups || agentGroups.length === 0) && (
          <Text style={{ color: '#ED1B2E', marginTop: '10px' } as any}>Đại lý này hiện tại không có nhóm để chọn</Text>
        )}
      </View>

      <View
        style={
          {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            marginTop: 30,
            justifyContent: 'center'
          } as any
        }
      >
        <button
          style={!allowCreate ? styles.disabledButton : styles.submitButton}
          type="button"
          onClick={handleCreate}
          disabled={isLoading || !allowCreate}
        >
          <span style={styles.mr_5}>
            <assets.PDCreate style={{ color: '#fff' }} />
          </span>
          Tạo
        </button>
      </View>
    </View>
  )
}
